<template>
    <transition :name="name">
        <slot />
    </transition>
</template>

<script>
/**
 * Wrapper component for Vue transitions used in floating panels.
 * Provides consistent transition animations across selection tool panels.
 *
 * Available transitions:
 * - fade: Opacity fade (mobile, with reduced motion on small screens)
 * - slide-up: Bottom sheet slide up (mobile)
 * - slide-right: Slide from right (desktop)
 * - slide-left: Slide from left (desktop)
 */
export default {
    name: 'PanelTransition',
    props: {
        name: {
            type: String,
            required: true,
            validator: v => ['fade', 'slide-up', 'slide-right', 'slide-left'].includes(v),
        },
    },
};
</script>

<style lang="scss">
/*
 * Transition styles are NOT scoped intentionally.
 * Vue's <transition> applies classes to slot content which comes from parent.
 * Scoped styles would not match due to unique hash attributes.
 */

/* Fade transition (mobile) */
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.3s ease;
}

.fade-enter,
.fade-leave-to {
    opacity: 0;
}

@media (max-width: 767px) {
    .fade-enter-active,
    .fade-leave-active {
        transition: none;
    }
}

/* Slide-up transition (mobile bottom sheet) */
.slide-up-enter-active,
.slide-up-leave-active {
    transition:
        transform 0.3s cubic-bezier(0.4, 0, 0.2, 1),
        opacity 0.3s ease;
}

.slide-up-enter,
.slide-up-leave-to {
    transform: translateY(100%);
    opacity: 0;
}

/* Slide-right transition (desktop) */
.slide-right-enter-active,
.slide-right-leave-active {
    transition:
        transform 0.25s ease,
        opacity 0.25s ease;
}

.slide-right-enter,
.slide-right-leave-to {
    transform: translateX(20px);
    opacity: 0;
}

/* Slide-left transition (desktop) */
.slide-left-enter-active,
.slide-left-leave-active {
    transition:
        transform 0.25s ease,
        opacity 0.25s ease;
}

.slide-left-enter,
.slide-left-leave-to {
    transform: translateX(-20px);
    opacity: 0;
}
</style>
